import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Send, Inbox, CheckCircle, Clock, Search, X, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPanel = ({ compact = false }) => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    recipient_name: '',
    recipient_type: '',
    subject: '',
    content: ''
  });

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
    fetchContacts();
  }, []);

  const fetchMessages = async () => {
    try {
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API}/messages/inbox`, { withCredentials: true }),
        axios.get(`${API}/messages/sent`, { withCredentials: true })
      ]);
      setMessages(inboxRes.data);
      setSentMessages(sentRes.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/messages/contacts`, { withCredentials: true });
      setContacts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setSearchResults(res.data);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const selectRecipient = (recipient) => {
    setNewMessage({
      ...newMessage,
      recipient_id: recipient.id,
      recipient_name: recipient.name || recipient.email,
      recipient_type: recipient.role || recipient.type
    });
    setSearchQuery(recipient.name || recipient.email);
    setSearchResults([]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.recipient_id || !newMessage.subject || !newMessage.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/messages`, {
        recipient_id: newMessage.recipient_id,
        recipient_type: newMessage.recipient_type,
        subject: newMessage.subject,
        content: newMessage.content
      }, { withCredentials: true });

      toast.success('Message sent successfully!');
      setComposeOpen(false);
      setNewMessage({ recipient_id: '', recipient_name: '', recipient_type: '', subject: '', content: '' });
      setSearchQuery('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read && message.recipient_id === user?.id) {
      markAsRead(message.id);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (compact) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Messages
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </CardTitle>
          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800">
                <Send className="w-4 h-4 mr-1" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New Message</DialogTitle>
              </DialogHeader>
              <form onSubmit={sendMessage} className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">To *</label>
                  {newMessage.recipient_id ? (
                    <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-emerald-50">
                      <span className="font-medium text-emerald-800">{newMessage.recipient_name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setNewMessage({ ...newMessage, recipient_id: '', recipient_name: '', recipient_type: '' });
                          setSearchQuery('');
                        }}
                        className="ml-auto text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchUsers(e.target.value);
                        }}
                        placeholder="Search for a user..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {searchResults.map((u) => (
                            <div
                              key={u.id}
                              onClick={() => selectRecipient(u)}
                              className="px-3 py-2 hover:bg-emerald-50 cursor-pointer"
                            >
                              <div className="font-medium">{u.name || u.email}</div>
                              <div className="text-xs text-slate-500">{u.role || u.type}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Show contacts from bookings */}
                  {!newMessage.recipient_id && contacts.length > 0 && searchQuery.length < 2 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                        <Users className="w-4 h-4" />
                        <span>Your Contacts</span>
                      </div>
                      <div className="space-y-1">
                        {contacts.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => selectRecipient(contact)}
                            className="flex items-center gap-3 px-3 py-2 border rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors"
                          >
                            {contact.picture ? (
                              <img src={contact.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                                {(contact.name || 'U')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">{contact.name}</div>
                              <div className="text-xs text-slate-500">{contact.type === 'school' ? '🏫 School' : '👤 Student'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!newMessage.recipient_id && contacts.length === 0 && searchQuery.length < 2 && (
                    <p className="mt-2 text-sm text-slate-500">
                      Type to search for users, or book a course to see school contacts here.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject *</label>
                  <input
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Message *</label>
                  <textarea
                    rows={4}
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                  Send Message
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4 text-slate-500">Loading...</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-4 text-slate-500">No messages yet</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {messages.slice(0, 5).map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => openMessage(msg)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    msg.is_read ? 'bg-slate-50 hover:bg-slate-100' : 'bg-emerald-50 hover:bg-emerald-100 border-l-4 border-emerald-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">{msg.sender_name}</span>
                    <span className="text-xs text-slate-500">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="text-sm font-semibold truncate">{msg.subject}</div>
                  <div className="text-xs text-slate-500 truncate">{msg.content}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full messages view
  return (
    <Card className="h-full">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Messages
          </CardTitle>
          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800">
                <Send className="w-4 h-4 mr-2" /> Compose
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New Message</DialogTitle>
              </DialogHeader>
              <form onSubmit={sendMessage} className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">To *</label>
                  {newMessage.recipient_id ? (
                    <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-emerald-50">
                      <span className="font-medium text-emerald-800">{newMessage.recipient_name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setNewMessage({ ...newMessage, recipient_id: '', recipient_name: '', recipient_type: '' });
                          setSearchQuery('');
                        }}
                        className="ml-auto text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchUsers(e.target.value);
                        }}
                        placeholder="Search for a user..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {searchResults.map((u) => (
                            <div
                              key={u.id}
                              onClick={() => selectRecipient(u)}
                              className="px-3 py-2 hover:bg-emerald-50 cursor-pointer"
                            >
                              <div className="font-medium">{u.name || u.email}</div>
                              <div className="text-xs text-slate-500">{u.role || u.type}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Show contacts from bookings */}
                  {!newMessage.recipient_id && contacts.length > 0 && searchQuery.length < 2 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                        <Users className="w-4 h-4" />
                        <span>Your Contacts</span>
                      </div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {contacts.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => selectRecipient(contact)}
                            className="flex items-center gap-3 px-3 py-2 border rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors"
                          >
                            {contact.picture ? (
                              <img src={contact.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                                {(contact.name || 'U')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">{contact.name}</div>
                              <div className="text-xs text-slate-500">{contact.type === 'school' ? '🏫 School' : '👤 Student'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!newMessage.recipient_id && contacts.length === 0 && searchQuery.length < 2 && (
                    <p className="mt-2 text-sm text-slate-500">
                      Type to search for users, or book a course to see school contacts here.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject *</label>
                  <input
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Message *</label>
                  <textarea
                    rows={4}
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                  Send Message
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            variant={activeTab === 'inbox' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('inbox')}
            className={activeTab === 'inbox' ? 'bg-emerald-700' : ''}
          >
            <Inbox className="w-4 h-4 mr-1" /> Inbox {unreadCount > 0 && `(${unreadCount})`}
          </Button>
          <Button
            variant={activeTab === 'sent' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('sent')}
            className={activeTab === 'sent' ? 'bg-emerald-700' : ''}
          >
            <Send className="w-4 h-4 mr-1" /> Sent
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 h-[500px]">
          {/* Message List */}
          <div className="border-r overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : (activeTab === 'inbox' ? messages : sentMessages).length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No {activeTab === 'inbox' ? 'messages' : 'sent messages'}
              </div>
            ) : (
              (activeTab === 'inbox' ? messages : sentMessages).map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => openMessage(msg)}
                  className={`p-4 border-b cursor-pointer transition-colors ${
                    selectedMessage?.id === msg.id ? 'bg-emerald-50' : ''
                  } ${
                    !msg.is_read && activeTab === 'inbox' ? 'bg-blue-50 font-semibold' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm truncate">
                      {activeTab === 'inbox' ? msg.sender_name : `To: ${msg.recipient_name}`}
                    </span>
                    <span className="text-xs text-slate-500">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium truncate">{msg.subject}</div>
                  <div className="text-xs text-slate-500 truncate mt-1">{msg.content}</div>
                </div>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="p-4 overflow-y-auto">
            {selectedMessage ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{selectedMessage.subject}</h3>
                  <button onClick={() => setSelectedMessage(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">
                    {selectedMessage.sender_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="font-medium">{selectedMessage.sender_name}</div>
                    <div className="text-xs text-slate-500">
                      To: {selectedMessage.recipient_name} • {new Date(selectedMessage.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNewMessage({
                        recipient_id: selectedMessage.sender_id,
                        recipient_name: selectedMessage.sender_name,
                        recipient_type: selectedMessage.sender_type,
                        subject: `Re: ${selectedMessage.subject}`,
                        content: ''
                      });
                      setSearchQuery(selectedMessage.sender_name);
                      setComposeOpen(true);
                    }}
                  >
                    Reply
                  </Button>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Select a message to read</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MessagesPanel;
